import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSnackbar } from 'notistack'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import LinearProgress from '@material-ui/core/LinearProgress'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { getVideoFileError, uploadVideo } from 'lib/cloudinary/upload-video'
import { useSubmitVirtualCoachingResponse } from './api-hooks'
import { useStyles } from './styles'
import {
  trackVirtualCoachingEvent,
  trackVirtualCoachingFormSubmit,
  VIRTUAL_COACHING_EVENTS,
} from './analytics'
import { getStudentName, isOnformUrl } from './utils'

const REPLY_MODE = {
  ONFORM: 'onform',
  UPLOAD: 'upload',
}

const ReplyForm = ({ request }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const fileInputRef = useRef(null)
  const [mode, setMode] = useState(REPLY_MODE.ONFORM)
  const [onformUrl, setOnformUrl] = useState('')
  const [file, setFile] = useState(null)
  const [uploadedUrl, setUploadedUrl] = useState(null)
  const [notes, setNotes] = useState('')
  const [uploadProgress, setUploadProgress] = useState(null)
  const [fieldError, setFieldError] = useState(null)

  const [
    submitResponse,
    { loading: isSubmitting },
  ] = useSubmitVirtualCoachingResponse({
    refetchQueries: ['VirtualCoachingRequests', 'VirtualCoachingSummary'],
  })

  const isBusy = isSubmitting || uploadProgress !== null
  const trimmedOnformUrl = onformUrl.trim()
  const trimmedNotes = notes.trim()

  const handleModeChange = event => {
    setMode(event.target.value)
    setFieldError(null)
  }

  const handleFileChange = event => {
    const selected = event.target.files?.[0]
    if (!selected) return
    const error = getVideoFileError(selected)
    if (error) {
      setFieldError(error)
      setFile(null)
      setUploadedUrl(null)
      event.target.value = ''
      return
    }
    setFieldError(null)
    setFile(selected)
    setUploadedUrl(null)
  }

  const validate = () => {
    if (mode === REPLY_MODE.ONFORM && trimmedOnformUrl) {
      if (!isOnformUrl(trimmedOnformUrl)) {
        return 'Paste the share link from Onform (link.getonform.com).'
      }
    }
    const hasResponse =
      (mode === REPLY_MODE.ONFORM && trimmedOnformUrl) ||
      (mode === REPLY_MODE.UPLOAD && file)
    if (!hasResponse && !trimmedNotes) {
      return mode === REPLY_MODE.ONFORM
        ? 'Add your Onform link or written notes before sharing.'
        : 'Upload your analysis video or add written notes before sharing.'
    }
    return null
  }

  const handleSubmit = async event => {
    event.preventDefault()
    const validationError = validate()
    setFieldError(validationError)
    if (validationError) return

    const input = {
      requestId: request.id,
      notes: trimmedNotes || null,
    }

    try {
      if (mode === REPLY_MODE.UPLOAD && file) {
        let url = uploadedUrl
        if (!url) {
          setUploadProgress(0)
          ;({ url } = await uploadVideo(file, {
            folder: 'virtual-coaching',
            onProgress: setUploadProgress,
          }))
          setUploadedUrl(url)
        }
        input.videoUrl = url
      } else if (mode === REPLY_MODE.ONFORM && trimmedOnformUrl) {
        input.onformUrl = trimmedOnformUrl
      }

      const { data } = await submitResponse({ variables: { input } })
      const result = data?.submitVirtualCoachingResponse

      if (!result?.success) {
        enqueueSnackbar(
          result?.message || 'Unable to share your analysis. Please try again.',
          { variant: 'error' },
        )
        return
      }

      enqueueSnackbar('Your analysis was shared with the student.', {
        variant: 'success',
      })
      trackVirtualCoachingFormSubmit('share-virtual-coaching-response')
      trackVirtualCoachingEvent(VIRTUAL_COACHING_EVENTS.REPLY_SHARED, {
        requestId: request.id,
        method: input.onformUrl
          ? 'onform'
          : input.videoUrl
            ? 'upload'
            : 'notes',
        submittedAt: request.submittedAt,
      })
    } catch (error) {
      enqueueSnackbar('Unable to share your analysis. Please try again.', {
        variant: 'error',
      })
    } finally {
      setUploadProgress(null)
    }
  }

  return (
    <Box className={classes.card} component="form" onSubmit={handleSubmit}>
      <Typography className={classes.cardTitle}>Send your analysis</Typography>
      <Typography className={classes.helper}>
        Analyze the swing in Onform and paste the share link here, or upload a
        video from another tool. {getStudentName(request.student)} will be
        notified as soon as you share it.
      </Typography>

      <RadioGroup
        row
        aria-label="How are you sending your analysis?"
        value={mode}
        onChange={handleModeChange}
        className={classes.formField}
      >
        <FormControlLabel
          value={REPLY_MODE.ONFORM}
          control={<Radio color="primary" />}
          label="Onform link"
          disabled={isBusy}
        />
        <FormControlLabel
          value={REPLY_MODE.UPLOAD}
          control={<Radio color="primary" />}
          label="Upload a video"
          disabled={isBusy}
        />
      </RadioGroup>

      {mode === REPLY_MODE.ONFORM ? (
        <TextField
          id="virtual-coaching-onform-url"
          className={classes.formField}
          label="Onform share link"
          placeholder="https://link.getonform.com/view?id=..."
          value={onformUrl}
          onChange={event => setOnformUrl(event.target.value)}
          disabled={isBusy}
          variant="outlined"
          fullWidth
          helperText="In Onform, share your analysis and copy the link. Come back here and paste it."
        />
      ) : (
        <Box className={classes.formField}>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            hidden
            onChange={handleFileChange}
            data-testid="virtual-coaching-reply-file"
          />
          <Button
            type="button"
            variant="outlined"
            color="primary"
            disabled={isBusy}
            onClick={() => fileInputRef.current?.click()}
          >
            {file ? 'Choose a different video' : 'Choose video'}
          </Button>
          {file ? (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              {file.name}
            </Typography>
          ) : null}
        </Box>
      )}

      <TextField
        id="virtual-coaching-notes"
        className={classes.formField}
        label="Notes for the student (optional)"
        value={notes}
        onChange={event => setNotes(event.target.value)}
        disabled={isBusy}
        variant="outlined"
        multiline
        rows={4}
        fullWidth
        helperText="Keep it simple. Focus on the one thing that will help most and give a clear next step."
      />

      {fieldError ? (
        <Typography color="error" variant="body2" role="alert" gutterBottom>
          {fieldError}
        </Typography>
      ) : null}

      {uploadProgress !== null ? (
        <Box mb={2}>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      ) : null}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={isBusy}
      >
        Share with student
      </Button>
    </Box>
  )
}

ReplyForm.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.string.isRequired,
    submittedAt: PropTypes.string,
    student: PropTypes.object,
  }).isRequired,
}

export default ReplyForm
